import React, {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Card,
  CardHeader,
  CardHeaderOwnProps,
  Chip,
  ChipOwnProps,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  AppFormikButtonTiles,
  AppFormikTextArea,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Formik, FormikProps, useFormikContext } from 'formik';
import { ReactComponent as Close } from 'assets/images/ic_close.svg';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { ReactComponent as Network } from 'assets/images/svgrepo/network-svgrepo-com (1) 1.svg';
import { ReactComponent as Time } from 'assets/images/svgrepo/time-svgrepo-com 1.svg';
import { ReactComponent as Line } from 'assets/images/svgrepo/line-monitor-svgrepo-com 1.svg';
import { ReactComponent as Key } from 'assets/images/svgrepo/key-svgrepo-com 1.svg';
import { ReactComponent as Lock } from 'assets/images/svgrepo/lock-svgrepo-com 1.svg';
import { ReactComponent as Warning } from 'assets/images/svgrepo/warning-svgrepo-com 1.svg';
import { ReactComponent as Shield } from 'assets/images/svgrepo/shield-01-svgrepo-com 1.svg';
// import { ReactComponent as View } from 'assets/images/svgrepo/view-svgrepo-com 1.svg';
import { ReactComponent as Edit } from 'assets/images/svgrepo/edit-[#1482].svg';
import { ReactComponent as Stars } from 'assets/images/svgrepo/stars-svgrepo-com 1.svg';
import { ReactComponent as Brains } from 'assets/images/svgrepo/brain-svgrepo-com 1.svg';
import { ReactComponent as Thunder } from 'assets/images/svgrepo/thunder-svgrepo-com 1.svg';
import { ReactComponent as Analytics } from 'assets/images/svgrepo/analytics-svgrepo-com 1.svg';
import { ReactComponent as Location } from 'assets/images/Location.svg';
import { ReactComponent as Trash } from 'assets/images/ic_trash.svg';
import { ReactComponent as DB } from 'assets/images/DB.svg';
import { ReactComponent as Back } from 'assets/images/ic_back.svg';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { ReactComponent as ApprovedCheck } from 'assets/images/approved-check.svg';

// eslint-disable-next-line import/no-unresolved
import { OverridableComponent } from '@mui/material/OverridableComponent';

import { DatasetsService } from 'services';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import LinkedAssessments from 'modules/ai-inventory/ai-system/ai-systems-detail/components/LinkedAssessments';
import { Toast, Utils } from 'shared/helpers';
import QuickRunAssessment from 'modules/ai-inventory/ai-system/ai-systems-detail/components/QuickRunAssessment';
import { BROADCAST_MESSAGES } from 'shared/Constants';
import ComingSoonCard from 'shared/components/ComingSoonCard';
import { TierApproverService } from 'services/TierApprovers';
import CommentsSection from 'modules/ai-inventory/ai-system/ai-systems-detail/components/CommentsSection';
import { EntityType } from 'shared/enums';
import AuditTrail from './components/AuditTrail';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import ClassIcon from '@mui/icons-material/Class';
import dayjs from 'dayjs';
import useSize from 'shared/hooks/useSize';

interface AuditTrailDataProps
  extends Omit<
    CardHeaderOwnProps,
    'titleTypographyProps' | 'subheaderTypographyProps'
  > {
  titleTypographyProps: ChipOwnProps & {
    component?: any;
  };
  subheaderTypographyProps: ChipOwnProps & {
    component?: any;
  };
}

export interface CardHeaderGlobeTypeMap<
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {},
  RootComponent extends React.ElementType = 'div'
> {
  props: AdditionalProps & AuditTrailDataProps;
  defaultComponent: RootComponent;
}
// eslint-disable-next-line @typescript-eslint/ban-types
type CardAuditTrail = OverridableComponent<
  // eslint-disable-next-line @typescript-eslint/ban-types
  CardHeaderGlobeTypeMap<{}, 'div'>
>;
interface datasetFormiksProps {
  recordsCategory: boolean[];
}
export interface IDataset {
  tenantId: null | string;
  _id: any;
  datasetName: string;
  owningTeam: string;
  contactPerson: string;
  purposeOfUse: string;
  lifecycleStage: string;
  sourceType: string;
  dataSensitivityClassification: string;
  approvalStatus: string;
  riskTier: string;
  watermarkingTechnique: string;
  sourceDetails: string;
  dataCollectionMethod: string;
  geographicOrigin: string;
  volumeSize: string;
  retentionPolicy: string;
  accessControls: string;
  encryption: string;
  sharingRestrictions: string;
  dataDictionaryLink: string;
  metadataDescription: string;
  knownDataQualityIssues: string;
  validationSummary: string;
  metadataAvailable: boolean;
  datasheetAvailable: boolean;
  dataModalities: string[];
  sensitiveDataTypesPresent: string[];
  complianceTags: string[];
  downstreamDependencies: string;
  reviewFrequency: string;
  monitoringInPlace: boolean;
  organizationId: null | string;
  createdBy: null | string;
  updatedBy: null | string;
  updatedDate: null | string;
  isActive: boolean;
  deletedAt: null | string;
  createdDate: string;
  __v: number;
}

const getStatusColors = (status: string): any => {
  switch (status) {
    case 'Pending':
      return {
        icon: (
          <QueryBuilderIcon
            sx={{
              color: '#1e40af',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#dbeafe',
      };
    case 'Draft':
      return {
        icon: (
          <FileCopyIcon
            sx={{
              color: '',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#f3f4f6',
      };
    case 'Archived':
      return {
        icon: (
          <ClassIcon
            sx={{
              color: '#92400e',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#fef3c7',
      };
    case 'Decommissioned':
      return {
        icon: (
          <NotInterestedIcon
            sx={{
              color: '#991b1b',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#fee2e2',
      };
    case 'Approved':
      return {
        icon: (
          <Box
            sx={{
              background: '#DCFCE7',
              width: '4.5rem',
              height: '4.5rem',
              alignContent: 'center',
              borderRadius: '100%',
            }}
            mb={2}
          >
            <ApprovedCheck />
          </Box>
        ),
        color: '#fef3c7',
      };
    default:
      return {
        icon: (
          <FileCopyIcon
            sx={{
              color: '',
              width: '65px',
              height: '65px',
            }}
          />
        ),
        color: '#f3f4f6',
      };
  }
};

const RecordDatasetDetalFormik = ({
  match,
  record,
  setRecordCategory,
  isLoading,
  recordsCategory,
  // metaDescriptionArray,
  state,
  recordId,
  userTier,
  actionOnChangeStatus,
  setIsFromAIInventory,
  anchorEl,
  open,
  handleClick,
  handleClose,
  statusMenus,
}: any): any => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setFieldValue } = useFormikContext();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  useEffect(() => {
    if (state?.from) {
      setFieldValue('recordsCategory', [
        false,
        false,
        false,
        false,
        true,
        false,
        false,
        false,
      ]);
    }
    if (state?.isFromAIInventory) {
      setIsFromAIInventory(state.isFromAIInventory);
    }
    navigate(location.pathname, { replace: true, state: null });
  }, []);

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction={match ? 'column' : 'row'}>
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          className="datasets-title mt-6 mb-3"
        >
          <Typography variant="h3">{record?.datasetName}</Typography>

          <Chip
            label={record?.approvalStatus}
            className={`record-status ${record?.approvalStatus}`}
          />
        </Stack>
        {false && userTier.includes(record?.riskTier) && (
          <>
            {record?.approvalStatus !== 'Pending' && (
              <AppPrimaryButton
                className="approveButton"
                title="Mark as Pending"
                onClick={() => {
                  actionOnChangeStatus('Pending');
                }}
              />
            )}
            {record?.approvalStatus === 'Pending' && (
              <Stack direction="row" spacing={2} mt={match ? 2 : 0}>
                <AppPrimaryButtonOutlined
                  className="approveButton"
                  title="Approve"
                  onClick={() => {
                    actionOnChangeStatus('Approved');
                  }}
                />
                <AppPrimaryButtonOutlined
                  className="rejectButton"
                  title="Reject"
                  onClick={() => {
                    actionOnChangeStatus('Rejected');
                  }}
                />
              </Stack>
            )}
          </>
        )}
      </Stack>

      <AppFormikButtonTiles
        variant="outlined"
        name="recordsCategory"
        size="small"
        label=""
        disabled={false}
        multichoice={false}
        className="app-group-button"
        buttons={[
          {
            value: 'Details',
            tooltip:
              'View and edit dataset metadata, data characteristics, types, and primary ownership information.',
            onClick: () => {
              setRecordCategory(true);
            },
          },
          {
            value: 'Quality & Validation',
            tooltip:
              'Monitor data completeness, accuracy metrics, and validation rule results.',
            onClick: () => {
              setRecordCategory(true);
            },
          },
          {
            value: 'Operations',
            tooltip:
              'Track data pipeline status, review frequency, monitoring, and execution jobs.',
            onClick: () => {
              setRecordCategory(true);
            },
          },
          {
            value: 'Security & Compliance',
            tooltip:
              'Manage data privacy settings, encryption, access policies, and regulatory compliance standards.',
            onClick: () => {
              setRecordCategory(true);
            },
          },
          {
            value: 'Risk Assessment',
            tooltip:
              'Evaluate potential data bias, security exposure, toxicity risks, and compliance impact levels.',
            onClick: () => {
              setRecordCategory(true);
            },
          },
          {
            value: 'Audit Trail',
            tooltip:
              'Review historical changes, access logs, schema updates, and administrative actions for this dataset.',
            onClick: () => {
              setRecordCategory(true);
            },
          },
          {
            value: 'Comments',
            tooltip:
              'Collaborate with team members, leave notes, and track internal discussions regarding the dataset.',
            onClick: () => {
              setRecordCategory(true);
            },
          },
          {
            value: 'Clarity Pipeline',
            tooltip:
              'Configure and monitor data transformation, enrichment, and processing pipelines within Clarity.',
            onClick: () => {
              setRecordCategory(true);
            },
          },
        ]}
        outerLabel
      />

      {isLoading && <CircularProgress />}

      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Box
          sx={{
            width: '100%',
          }}
        >
          {recordsCategory === 0 && (
            <Box className="record-container">
              <Box className="record-container-title">
                <Typography variant="h4">Dataset Details</Typography>
              </Box>
              <Box className="container-wrapper">
                <Stack direction="column" spacing={3}>
                  <Box className="information-content">
                    <Typography variant="h6">Basic Information</Typography>
                    <Stack direction="row">
                      <Box className="content-detail">
                        <Typography variant="body2">Owning Team</Typography>
                        <Typography variant="body1" component="span">
                          {record?.owningTeam ?? '-'}
                        </Typography>
                      </Box>
                      <Box className="content-detail">
                        <Typography variant="body2">Contact Person</Typography>
                        <Typography variant="body1" component="span">
                          {record?.contactPerson ?? '-'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row">
                      <Box className="content-detail">
                        <Typography variant="body2">Lifecycle Stage</Typography>
                        <Typography variant="body1" component="span">
                          {record?.lifecycleStage ?? '-'}
                        </Typography>
                      </Box>
                      <Box className="content-detail">
                        <Typography variant="body2">Source Type</Typography>
                        <Typography variant="body1" component="span">
                          {record?.sourceType ?? '-'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row">
                      <Box className="content-detail">
                        <Typography variant="body2">Risk Tier</Typography>
                        {record?.riskTier ? (
                          <Chip
                            label={record?.riskTier}
                            className="risk-tier"
                          />
                        ) : (
                          <Typography>-</Typography>
                        )}
                      </Box>
                      <Box className="content-detail">
                        <Typography variant="body2">Approval Status</Typography>
                        {record?.approvalStatus ? (
                          <Chip
                            label={record?.approvalStatus}
                            className={
                              record?.approvalStatus === 'Approved'
                                ? 'approval-status'
                                : ''
                            }
                          />
                        ) : (
                          <Typography>-</Typography>
                        )}
                      </Box>
                    </Stack>

                    <Box className="content-detail">
                      <Typography variant="body2">
                        Data Sensitivity Classification
                      </Typography>
                      {record?.dataSensitivityClassification ? (
                        <Chip
                          label={record?.dataSensitivityClassification}
                          className="classification"
                        />
                      ) : (
                        <Typography>-</Typography>
                      )}
                    </Box>
                  </Box>
                  <Box className="characteristics-content">
                    <Typography variant="h6">Data Characteristics</Typography>
                    <Stack direction="row">
                      <Box className="content-detail">
                        <Typography variant="body2">
                          Data Collection Method
                        </Typography>
                        <Typography variant="body1" component="span">
                          {record?.dataCollectionMethod
                            ? record?.dataCollectionMethod
                            : '-'}
                        </Typography>
                      </Box>
                      <Box className="content-detail">
                        <Stack direction="row" spacing={2}>
                          <SvgIcon
                            component={Location}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#5c5c5d"
                          />
                          <Typography variant="body2">
                            Geographic Origin
                          </Typography>
                        </Stack>

                        <Typography variant="body1" component="span">
                          {record?.geographicOrigin
                            ? record?.geographicOrigin
                            : '-'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row">
                      <Box className="content-detail">
                        <Stack direction="row" spacing={2}>
                          <SvgIcon
                            component={DB}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#5c5c5d"
                          />
                          <Typography variant="body2">Volume Size</Typography>
                        </Stack>
                        <Typography variant="body1" component="span">
                          {record?.volumeSize ? record?.volumeSize : '-'}
                        </Typography>
                      </Box>
                      <Box className="content-detail">
                        <Typography variant="body2">
                          Watermarking Technique
                        </Typography>
                        {record?.watermarkingTechnique ? (
                          <Chip
                            label={record?.watermarkingTechnique}
                            className="watermarking-chips"
                          />
                        ) : (
                          <Typography>-</Typography>
                        )}
                      </Box>
                    </Stack>
                    {/* <Box className="content-detail">
                  <Typography variant="body2">Business Goals</Typography>
                  <Typography variant="body1" component="span">
                    The office
                  </Typography>
                </Box>
                <Box className="content-detail">
                  <Typography variant="body2">Business Value</Typography>
                  <Typography variant="body1" component="span">
                    The office
                  </Typography>
                </Box> */}
                  </Box>
                  <Box className="documentation-content">
                    <Typography variant="h6">Documentation</Typography>
                    <Stack direction="row">
                      <Box className="content-detail">
                        <Typography variant="body2">
                          Metadata Available
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          <IconButton>
                            <SvgIcon
                              component={Close}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                          </IconButton>
                          <Chip
                            label={record?.metadataAvailable ? 'Yes' : 'No'}
                          />
                        </Stack>
                      </Box>
                      <Box className="content-detail">
                        <Typography variant="body2">
                          Datasheet Available
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          <IconButton>
                            <SvgIcon
                              component={Close}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                          </IconButton>
                          <Chip
                            label={record?.datasheetAvailable ? 'Yes' : 'No'}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>

                  <Box className="datatypes-content">
                    <Typography variant="h6">Data Types</Typography>
                    <Stack direction="row">
                      {/* <Box className="content-detail">
                        <Typography variant="body2">
                          Metadata Available
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          {metaDescriptionArray &&
                          metaDescriptionArray.length === 0 ? (
                            metaDescriptionArray.map((t: any) => (
                              <Chip label={t} />
                            ))
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </Stack>
                      </Box> */}
                      <Box className="content-detail">
                        <Typography variant="body2">Data Modalities</Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          {record?.dataModalities &&
                          record.dataModalities.length > 0 ? (
                            record.dataModalities.map(
                              (modality: string, index: number) => (
                                <Chip label={modality} key={index} />
                              )
                            )
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </Stack>
                      </Box>
                      <Box className="content-detail">
                        <Typography variant="body2">
                          Sensitive Data Types
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          {record?.sensitiveDataTypesPresent &&
                          record.sensitiveDataTypesPresent.length > 0 ? (
                            record.sensitiveDataTypesPresent.map(
                              (sensitive: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={sensitive}
                                  className="data-type-chip"
                                />
                              )
                            )
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                  <Box className="purpose-source-content">
                    <Typography variant="h6">Purpose & Source</Typography>
                    <Box className="content-detail block">
                      <Typography variant="body2">Purpose of Use</Typography>
                      <Typography variant="body1" component="div">
                        {record?.purposeOfUse ?? '-'}
                      </Typography>
                    </Box>
                    <Box className="content-detail block">
                      <Typography variant="body2">Source Details</Typography>
                      <Typography variant="body1" component="div">
                        {record?.sourceDetails ? record?.sourceDetails : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
          {recordsCategory === 1 && (
            <Box className="record-container">
              <Box className="record-container-title">
                <Typography variant="h4">Data Quality & Validation</Typography>
              </Box>
              <Box className="container-wrapper">
                <Stack direction="column" spacing={3}>
                  <Box className="data-quality-validation">
                    <Box className="content-detail block">
                      <Typography variant="body2">
                        Known Data Quality Issues
                      </Typography>
                      <Typography variant="body1" component="div">
                        {record?.knownDataQualityIssues
                          ? record?.knownDataQualityIssues
                          : '-'}
                      </Typography>
                    </Box>
                    <Box className="content-detail block">
                      <Typography variant="body2">
                        Validation Summary
                      </Typography>
                      <Typography variant="body1" component="div">
                        {record?.validationSummary
                          ? record?.validationSummary
                          : '-'}
                      </Typography>
                    </Box>
                    <Box className="content-detail">
                      <Typography variant="body2">
                        Bias & Fairness Assessment
                      </Typography>
                      <Stack direction="row">
                        <IconButton>
                          <SvgIcon
                            component={Close}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#3D3C42"
                          />
                        </IconButton>
                        <Chip label="Not Completed" />
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
          {recordsCategory === 2 && (
            <Box className="record-container">
              <Box className="record-container-title">
                <Typography variant="h4">Operations & Monitoring</Typography>
              </Box>
              <Box className="container-wrapper">
                <Stack direction="column" spacing={3}>
                  <Box className="data-operation-monitoring">
                    <Box className="content-detail block">
                      <Stack direction="row" spacing={2}>
                        <SvgIcon
                          component={Network}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                        <Typography variant="body2">
                          Downstream Dependencies
                        </Typography>
                      </Stack>
                      <Typography variant="body1" component="div">
                        {record?.downstreamDependencies
                          ? record?.downstreamDependencies
                          : 'No downstream dependencies documented'}
                      </Typography>
                    </Box>
                    <Box className="content-detail block">
                      <Stack direction="row" spacing={2}>
                        <SvgIcon
                          component={Time}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                        <Typography variant="body2">
                          Review Frequency
                        </Typography>
                      </Stack>

                      <Typography variant="body1" component="div">
                        {record?.reviewFrequency
                          ? record?.reviewFrequency
                          : 'Not specified'}
                      </Typography>
                    </Box>
                    <Box className="content-detail block">
                      <Stack direction="row" spacing={2}>
                        <SvgIcon
                          component={Line}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                        <Typography variant="body2">
                          Monitoring in Place
                        </Typography>
                      </Stack>
                      {/* <Stack direction="row" spacing={2}>
                    <IconButton>
                      <SvgIcon
                        component={Close}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="#3D3C42"
                      />
                    </IconButton>
                    <Chip label={record?.monitoringInPlace ? 'Yes' : 'No'} />
                  </Stack> */}
                      <Typography variant="body1" component="div">
                        {record?.monitoringInPlace
                          ? record?.monitoringInPlace
                          : 'No monitoring details specified'}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
          {recordsCategory === 3 && (
            <Box className="record-container">
              <Box className="record-container-title">
                <Typography variant="h4">Security & Compliance</Typography>
              </Box>
              <Box className="container-wrapper">
                <Stack direction="column" spacing={3}>
                  <Box className="security-controls">
                    <Typography variant="h6">Security Controls</Typography>
                    <Box className="content-detail block">
                      <Stack direction="row" spacing={2}>
                        <SvgIcon
                          component={Key}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                        <Typography variant="body2">Access Controls</Typography>
                      </Stack>

                      <Typography variant="body1" component="div">
                        {record?.accessControls ? record?.accessControls : '-'}
                      </Typography>
                    </Box>
                    <Box className="content-detail block">
                      <Stack direction="row" spacing={2}>
                        <SvgIcon
                          component={Lock}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                        <Typography variant="body2">Encryption</Typography>
                      </Stack>
                      <Typography variant="body1" component="div">
                        {record?.encryption ? record?.encryption : '-'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="security-controls">
                    <Typography variant="h6">Data Governance</Typography>

                    <Box className="content-detail block">
                      <Stack direction="row" spacing={2}>
                        <SvgIcon
                          component={Warning}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                        <Typography variant="body2">
                          Sharing Restrictions
                        </Typography>
                      </Stack>
                      <Typography variant="body1" component="div">
                        {record?.sharingRestrictions
                          ? record?.sharingRestrictions
                          : '-'}
                      </Typography>
                    </Box>
                    <Box className="content-detail block">
                      <Stack direction="row" spacing={2}>
                        <SvgIcon
                          component={Shield}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                        <Typography variant="body2">
                          Retention Policy
                        </Typography>
                      </Stack>
                      <Typography variant="body1" component="div">
                        {record?.retentionPolicy
                          ? record?.retentionPolicy
                          : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
          {recordsCategory === 4 && (
            <LinkedAssessments
              type="Dataset"
              datasetId={recordId}
              showSendAssessment={false}
              onClickSendAssessment={() => {}}
            />
          )}
          {recordsCategory === 5 && (
            <>
              <AuditTrail title="Audit Trail" entityType={EntityType.DATASET} />
            </>
          )}
          {recordsCategory === 6 && (
            <>
              <CommentsSection entityType={EntityType.DATASET} />

              {false && (
                <Box className="record-container">
                  <Box className="record-container-title">
                    <Typography variant="h4">Comments (0)</Typography>
                  </Box>
                  <Box className="container-wrapper">
                    <Stack
                      direction="column"
                      spacing={2}
                      className="record-comment"
                    >
                      <Typography variant="body1">
                        Discussion and notes about mdtyncg
                      </Typography>
                      <AppFormikTextArea
                        name="comments"
                        placeholder="Write a comment..."
                        outerLabel={false}
                        label=""
                      />
                      <AppPrimaryButton title="Comment" />
                    </Stack>
                    <Box className="commentList">
                      <Typography variant="body2" component="span">
                        No comments yet. Be the first to start the discussion!
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
          {recordsCategory === 7 && (
            <>
              <ComingSoonCard title="Clarity Pipeline" />
              {false && (
                <Box className="record-container pipeline">
                  <Box className="record-container-title">
                    <Chip
                      label="Premium Feature"
                      icon={
                        <SvgIcon
                          component={Stars}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                      }
                    />
                    <Typography variant="h4">
                      Unlock Clarity Pipeline
                    </Typography>
                    <Typography variant="body1" component="span">
                      Transform your data management with AI-powered insights,
                      advanced governance, and intelligent automation
                    </Typography>
                  </Box>
                  <Box className="container-wrapper">
                    <Stack direction={match ? 'column' : 'row'}>
                      <Card variant="outlined">
                        <CardHeader
                          avatar={
                            <SvgIcon
                              component={Brains}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                          }
                          title="AI-Powered Data Discovery"
                          subheader="Automatically discover and catalog data assets across your organisation with advanced AI algorithms."
                        />
                      </Card>
                      <Card variant="outlined">
                        <CardHeader
                          avatar={
                            <SvgIcon
                              component={Shield}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                          }
                          title="Enhanced Data Governance"
                          subheader="Advanced compliance monitoring, automated policy enforcement, and real-time governance controls."
                        />
                      </Card>
                    </Stack>
                    <Stack direction={match ? 'column' : 'row'}>
                      <Card variant="outlined">
                        <CardHeader
                          avatar={
                            <SvgIcon
                              component={Thunder}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                          }
                          title="Intelligent Data Quality"
                          subheader="AI-driven data quality assessment, anomaly detection, and automated remediation suggestions."
                        />
                      </Card>
                      <Card variant="outlined">
                        <CardHeader
                          avatar={
                            <SvgIcon
                              component={Analytics}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                          }
                          title="Advanced Analytics"
                          subheader="Deep insights into data lineage, usage patterns, and predictive data management recommendations."
                        />
                      </Card>
                    </Stack>
                    <Box className="headline-record-container">
                      <Typography variant="h5">
                        Why Choose Clarity Pipeline?
                      </Typography>
                      <Stack className="headline-record" direction="row">
                        <Box className="headline-content">
                          <Typography variant="body1" component="span">
                            90%
                          </Typography>
                          <Typography variant="body2" component="span">
                            Reduction in manual data cataloging
                          </Typography>
                        </Box>
                        <Box className="headline-content">
                          <Typography variant="body1" component="span">
                            5x
                          </Typography>
                          <Typography variant="body2" component="span">
                            Faster compliance reporting
                          </Typography>
                        </Box>
                        <Box className="headline-content">
                          <Typography variant="body1" component="span">
                            99.9%
                          </Typography>
                          <Typography variant="body2" component="span">
                            Data quality accuracy
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box className="upgrade-record-container">
                      <Typography variant="h6">
                        Ready to Transform Your Data Management?
                      </Typography>
                      <Typography variant="body2">
                        Join leading organisations who have already upgraded to{' '}
                        <Typography variant="body2" component="span">
                          {' '}
                          Clarity Pipeline{' '}
                        </Typography>
                        and are experiencing the future of intelligent data
                        governance.
                      </Typography>
                      <AppPrimaryButton title="Upgrade to Clarity Pipeline" />
                      <Typography variant="body1" component="span">
                        No commitment required • Free consultation included
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
        {recordsCategory === 0 && (
          <Box>
            {userTier.includes(record?.riskTier) &&
              orgACLConfig.aiInventory?.includes(
                'aiInventory.datasets.view.approval'
              ) && (
                <Box
                  sx={{
                    padding: '10px',
                    background: 'white',
                    borderRadius: '10px',
                    minWidth: '17.5rem',
                    height: 'max-content',
                    marginTop: '16px',
                  }}
                >
                  <Stack direction="column" spacing={1}>
                    <Stack spacing={2}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '24px !important',
                          fontWeight: '700 !important',
                        }}
                      >
                        Approval Status
                      </Typography>
                      <Typography variant="body1" className="text-gray">
                        Current approval state of this dataset
                      </Typography>
                    </Stack>

                    <Box sx={{ textAlign: 'center' }} className="my-5">
                      <Box sx={{ justifyItems: 'center' }} className="mb-5">
                        {getStatusColors(record?.approvalStatus)?.icon}
                      </Box>
                      <Typography className="f20 fw-7 mb-5">
                        {record?.approvalStatus}
                      </Typography>

                      {record?.approvalStatus === 'Approved' && (
                        <Typography
                          variant="body1"
                          className="text-gray mb-5"
                          mt={2}
                          fontWeight={600}
                        >
                          Approved on{' '}
                          {dayjs(record?.updatedDate ?? dayjs()).format(
                            'DD/MM/YYYY'
                          )}
                        </Typography>
                      )}

                      {/* Dropdown now always visible since card is already gated */}
                      <Box
                        onClick={handleClick}
                        className="solutions-approval-status cursor"
                        sx={{ cursor: 'pointer' }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{
                            alignItems: 'center',
                            border: '1px solid #f3f4f6',
                            borderRadius: '4px',
                            padding: '8px 10px',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '14px !important' }}
                          >
                            Change approval status
                          </Typography>
                          <KeyboardArrowDownIcon />
                        </Stack>
                      </Box>

                      <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                      >
                        {statusMenus.map((status: string) => (
                          <MenuItem
                            key={status}
                            onClick={() => {
                              actionOnChangeStatus(status);
                              handleClose();
                            }}
                          >
                            {status}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  </Stack>
                </Box>
              )}
          </Box>
        )}
      </Box>
    </Stack>
  );
};

const CardAuditTrail: CardAuditTrail = CardHeader;
const RecordDatasetDetail = (): ReactElement => {
  const { isMobile } = useSize();
  const [isRecordCategory, setRecordCategory] = useState<boolean>(false);
  const theme = useTheme();
  const match = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [record, setRecordData] = useState<IDataset | null>(null);
  const { recordId } = useParams<any>();
  const [openDeleteDataset, setOpenDeleteDataset] = React.useState(false);
  const [showRunAssessment, setShowRunAssessment] = useState(false);
  const location = useLocation();
  const { state } = location;
  const [userTier, setUserTier] = useState<string[]>([]);
  const [isFromAIInventory, setIsFromAIInventory] = useState(null);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [editTierLevel, setEditTierLevel] = useState(false);

  const formikRef = useRef<FormikProps<datasetFormiksProps> | null>(null);
  const recordsCategory = useMemo(() => {
    const { current: formik } = formikRef;
    if (formik) {
      const recordCategoryIndex = formik.values.recordsCategory.findIndex(
        (t) => t === true
      );
      return recordCategoryIndex;
    }
    return isLoading ? -1 : 0;
  }, [isRecordCategory, isLoading]);

  const statusMenus = useMemo(() => ['Pending', 'Approved', 'Rejected'], []);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setOpen(false);
  };

  // API Calls
  const loadUserTier = (): void => {
    TierApproverService.getUserTier()
      .then((response) => {
        const { data } = response.data;
        if (data.length > 0) setUserTier(data);
        else setUserTier([]);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };
  const loadDatasetDetail = (): void => {
    DatasetsService.getSingleDataset(recordId ?? null)
      .then((response) => {
        const { data: resp } = response;
        if (resp && resp.data) {
          setRecordData(resp.data);
          // eslint-disable-next-line @typescript-eslint/no-shadow
          // const { data } = response;
          // if (data) {
          //     const { data: inventoryData } = data;
          //     setData(inventoryData);
          // }
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(true);
      });
  };

  // UI Actions
  // const metaDescriptionArray = useMemo(
  //   () => (record ? record.metadataDescription?.split(',') : []),
  //   [record, isLoading]
  // );

  const actionOnChangeStatus = (status: string): void => {
    DatasetsService.updateDatasetStatus({}, `${recordId}`, status)
      .then(() => {
        loadDatasetDetail();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const handleCloseDeleteDataset = (): void => {
    setOpenDeleteDataset(false);
  };

  const handleDeleteDataset = (id: any): void => {
    DatasetsService.deleteDatasets(id)
      .then(() => {
        Toast.showSuccessMessage(`Successfully delete datasets`);
        setOpenDeleteDataset(false);
        navigate(-1);
      })
      .catch((e) => {
        Toast.showErrorMessage(e);
      });
  };

  // Hooks
  useEffect(() => {
    if (recordId) {
      loadDatasetDetail();
    }
  }, [recordId]);

  useLayoutEffect(() => {
    if (isRecordCategory) {
      setRecordCategory(false);
    }
  }, [isRecordCategory]);

  useEffect(() => {
    loadUserTier();
  }, []);

  return (
    <Box className="ai-inventory datasets mt-4">
      <HeadTag title="AI Systems" />
      <Stack
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
      >
        <AppPrimaryButton
          // title="Back to Datasets"
          title={isMobile ? 'Back to AI System' : ''}
          startIcon={
            <SvgIcon
              component={Back}
              inheritViewBox
              className="view-icon"
              fontSize="inherit"
              htmlColor="white"
            />
          }
          onClick={() => {
            if (isFromAIInventory) {
              navigate(`/ai-systems/records/${isFromAIInventory}`, {
                state: { isFromDataset: true },
              });
            } else {
              navigate(-1);
            }
          }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {orgACLConfig.aiInventory?.includes(
            'aiInventory.datasets.view.editDataset'
          ) && (
            <AppPrimaryButton
              title="Edit"
              startIcon={
                <SvgIcon
                  component={Edit}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                  htmlColor="white"
                />
              }
              onClick={() => {
                navigate(`/datasets/edit/${recordId}`);
              }}
              sx={{ whiteSpace: 'nowrap' }}
            />
          )}
          {orgACLConfig.aiInventory?.includes(
            'aiInventory.datasets.view.deleteDataset'
          ) && (
            <AppPrimaryButton
              title="Delete"
              className="btn-danger"
              startIcon={
                <SvgIcon
                  component={Trash}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                  htmlColor="white"
                />
              }
              onClick={() => {
                setOpenDeleteDataset(true);
              }}
            />
          )}
          {orgACLConfig.aiInventory?.includes(
            'aiInventory.datasets.view.runRiskAssessment'
          ) && (
            <AppPrimaryButtonOutlined
              title="Run Risk Assessment"
              startIcon={<PlayCircleOutlineIcon />}
              onClick={() => {
                setShowRunAssessment(true);
              }}
            />
          )}
          {orgACLConfig.aiInventory?.includes(
            'aiInventory.datasets.view.createRisk'
          ) && (
            <AppPrimaryButton
              title="Create Risk"
              startIcon={
                <SvgIcon
                  component={Plus}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                  htmlColor="white"
                />
              }
              onClick={() => {
                navigate('/risk-register/add');
              }}
            />
          )}
        </Stack>
      </Stack>

      <Box className="ai-solutions dataset-details">
        <Formik
          innerRef={formikRef}
          initialValues={{
            recordsCategory: [
              true,
              false,
              false,
              false,
              false,
              false,
              false,
              false,
            ],
          }}
          onSubmit={() => console.log('test')}
        >
          <RecordDatasetDetalFormik
            match={match}
            record={record}
            setRecordCategory={setRecordCategory}
            isLoading={isLoading}
            recordsCategory={recordsCategory}
            // metaDescriptionArray={metaDescriptionArray}
            state={state}
            recordId={recordId}
            userTier={userTier}
            actionOnChangeStatus={actionOnChangeStatus}
            setIsFromAIInventory={setIsFromAIInventory}
            anchorEl={anchorEl}
            open={open}
            handleClick={handleClick}
            handleClose={handleClose}
            statusMenus={statusMenus}
            editTierLevel={editTierLevel}
            setEditTierLevel={setEditTierLevel}
          />
        </Formik>
      </Box>

      <ConfirmDialog
        title="Are you absolutely sure?"
        subTitle={
          <>
            This action cannot be undone. This will permanently delete the
            dataset <b>{record?.datasetName}</b> and all associated records.
          </>
        }
        showConfirm={openDeleteDataset}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteDataset();
          handleDeleteDataset(recordId);
        }}
        onCancel={handleCloseDeleteDataset}
        onCloseDialog={handleCloseDeleteDataset}
      />

      {showRunAssessment && (
        <QuickRunAssessment
          assessmentFor={recordId}
          type="Dataset"
          open={showRunAssessment}
          onCloseAssessment={(reload: boolean) => {
            if (reload) {
              window.postMessage(BROADCAST_MESSAGES.RELOAD_ASSESSMENTS);
            }
            setShowRunAssessment(false);
          }}
        />
      )}
    </Box>
  );
};

export default RecordDatasetDetail;
